import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import { Check, Loader2, Undo2, UserCheck } from 'lucide-react';

import { cn } from '../../../lib/utils';
import ButtonName from './ButtonName';

// The VA.gov access step (Nic 2026-10-25): all we ask of the specialist is
// to confirm they saw the client sign in to VA.gov, one button, nothing to
// interpret. Above it, what's happened so far as a short history (only what
// did happen). Sending them back to set it up lives here too, said plainly.
// Their Confirm is what earns Envoy's $250 (lib/envoyCommissions).

// Shared with the Verify POA step (PoaStep.js, Nic 2026-10-27): the same
// card, button and link looks, and the same History list.
export const LIFT =
  'bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_14px_-4px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/[0.05] dark:!bg-slate-800/60 dark:shadow-none dark:ring-white/10';
export const BTN_DARK =
  'inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-2 text-[13px] font-semibold text-[#fff] outline-none hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 disabled:cursor-not-allowed disabled:opacity-50 dark:!bg-white dark:text-slate-900 dark:hover:!bg-slate-200';
export const LINK =
  'inline-flex items-center gap-1 rounded border-0 bg-transparent p-0 text-[12.5px] font-medium text-slate-500 outline-none hover:text-slate-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 disabled:opacity-50 dark:text-slate-400 dark:hover:text-slate-100';

const day = (at) => (at ? moment(at).format('MMM D') : null);
const SAID = { yes: 'they have VA.gov', no: "they don't have VA.gov", unsure: "they weren't sure about VA.gov" };

// What happened, oldest first. Only real events: nothing is listed as
// "not yet".
// Send backs and their "I've done it" come from the client's events (Nic
// 2026-10-25: the send back vanished once they tapped, because the stage
// only carries the latest), so every round shows; the stage's dates are
// the fallback.
export const vaGovHistory = ({ va = {}, confirmedMeta = null, confirmed = false, events = [] }) => {
  const out = [];
  if (va.intake && SAID[va.intake]) {
    out.push({ key: 'intake', tone: va.intake === 'yes' ? 'good' : 'quiet', text: `At sign-up, they said ${SAID[va.intake]}` });
  }
  if (va.confirmed && SAID[va.confirmed]) {
    const who = va.confirmedByRep ? `${va.confirmedByRep} (Sales)` : 'Their Sales Rep';
    const text =
      va.confirmed === 'yes'
        ? `${who} confirmed they have VA.gov`
        : `${who} heard ${SAID[va.confirmed]}`;
    out.push({ key: 'sales', at: va.confirmedAt, tone: va.confirmed === 'yes' ? 'good' : 'quiet', text });
  }
  const sentBack = (events || []).filter((e) => e.type === 'va_gov_sent_back');
  const tapped = (events || []).filter((e) => e.type === 'client_va_gov_done');
  sentBack.forEach((e) =>
    out.push({
      key: `back-${e.id || e.at}`,
      at: e.at,
      tone: 'warn',
      text: e.actor ? `${e.actor} sent them back to set up VA.gov` : 'Sent back to set up VA.gov',
    }),
  );
  tapped.forEach((e) =>
    out.push({ key: `client-${e.id || e.at}`, at: e.at, tone: 'good', text: "They said it's set up (in their portal)" }),
  );
  if (!sentBack.length && va.bouncedAt) {
    out.push({ key: 'back', at: va.bouncedAt, tone: 'warn', text: 'Sent back to set up VA.gov' });
  }
  if (!tapped.length && va.clientSaidDoneAt) {
    out.push({ key: 'client', at: va.clientSaidDoneAt, tone: 'good', text: "They said it's set up (in their portal)" });
  }
  if (confirmed) {
    out.push({
      key: 'confirmed',
      at: confirmedMeta?.at || null,
      tone: 'done',
      text: `${confirmedMeta?.name || 'Their specialist'} saw them sign in`,
    });
  }
  // What they said at sign-up first (it has no date), then in time order.
  const t = (x) => (x.at ? new Date(x.at).getTime() : Number.MAX_SAFE_INTEGER);
  return [...out.filter((x) => x.key === 'intake'), ...out.filter((x) => x.key !== 'intake').sort((a, b) => t(a) - t(b))];
};

const DOT = {
  good: 'bg-emerald-500',
  quiet: 'bg-slate-300 dark:bg-slate-600',
  warn: 'bg-amber-500',
  done: 'bg-emerald-500 ring-4 ring-emerald-500/15',
};

export const History = ({ items }) => (
  <ol className="relative mb-0 list-none space-y-[10px] pl-0">
    {items.length > 1 ? (
      <span aria-hidden className="absolute bottom-[8px] left-[4px] top-[8px] w-px bg-slate-200 dark:!bg-white/10" />
    ) : null}
    {items.map((it) => (
      <li key={it.key} className="relative flex items-baseline gap-[12px] text-[13px]">
        <span className={cn('relative top-[-1px] h-[9px] w-[9px] shrink-0 rounded-full', DOT[it.tone])} />
        <span
          className={cn(
            'min-w-0 flex-1',
            it.tone === 'done' ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300',
          )}
        >
          {it.text}
        </span>
        {it.at ? <span className="shrink-0 text-[11.5px] tabular-nums text-slate-400">{day(it.at)}</span> : null}
      </li>
    ))}
  </ol>
);

const SEND_BACK_STEPS = [
  <>
    Go to va.gov and click <ButtonName>Sign in</ButtonName> at the top of the page.
  </>,
  'No account yet? Create one with CLEAR, ID.me or Login.gov. Any of the three works.',
  <>
    Once they can sign in, they tap <ButtonName>I&apos;ve done it</ButtonName> in their Veritas Ratings client
    dashboard, or reply to our text. Then book another call.
  </>,
];

const SendBackDialog = ({ busy, onCancel, onConfirm }) =>
  createPortal(
    <div data-ws-modal className="fixed inset-0 z-[1080] flex items-center justify-center bg-slate-900/40 p-[16px]">
      <div className="w-full max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-2xl dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10">
        <div className="px-[22px] pb-[6px] pt-[20px]">
          <div className="text-base font-bold text-slate-900 dark:text-white">Send them back to set up VA.gov</div>
          <p className="mb-0 mt-[4px] text-sm text-slate-500 dark:text-slate-400">
            They move to Setting up VA.gov until they tap <ButtonName>I&apos;ve done it</ButtonName>. Tell them on the
            call:
          </p>
        </div>
        <ol className="mb-0 list-none space-y-[10px] px-[22px] py-[12px] pl-[22px]">
          {SEND_BACK_STEPS.map((t, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} className="flex gap-[10px] text-sm text-slate-700 dark:text-slate-200">
              <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 dark:!bg-white/10 dark:text-slate-300">
                {i + 1}
              </span>
              <span>{t}</span>
            </li>
          ))}
          <li className="pl-[32px] text-xs text-slate-500 dark:text-slate-400">
            Stuck? VA.gov&apos;s support line is 866-279-3677.
          </li>
        </ol>
        <div className="flex justify-end gap-[8px] px-[22px] pb-[18px] pt-[12px]">
          <button
            type="button"
            onClick={onCancel}
            className="h-[36px] rounded-lg px-[14px] text-sm font-semibold text-slate-600 outline-none hover:bg-slate-100 focus:outline-none dark:text-slate-300 dark:hover:!bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="inline-flex h-[36px] items-center gap-[6px] rounded-lg bg-slate-900 px-[14px] text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-50 dark:!bg-white dark:!text-slate-900"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Send them back
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

const VaGovStep = ({
  va,
  firstName,
  confirmed,
  confirmedMeta,
  events = [],
  canAct,
  saving = false,
  sendingBack = false,
  onConfirm,
  onUndo,
  onSendBack,
}) => {
  const [asking, setAsking] = useState(false);
  const who = firstName || 'them';
  const settingUp = va?.stage === 'waiting';
  const items = vaGovHistory({ va: va || {}, confirmedMeta, confirmed, events });
  return (
    <div className="mt-1.5">
      <p className="mb-0 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
        Confirm once you&apos;ve seen {who} sign in to VA.gov on a call.
      </p>

      <div className={cn('mt-[16px] rounded-2xl p-[18px]', LIFT)}>
        {items.length ? (
          <History items={items} />
        ) : (
          <p className="mb-0 text-[13px] italic text-slate-400">Nothing about VA.gov on record yet.</p>
        )}

        <div className="mt-[16px] border-0 border-t border-solid border-slate-100 pt-[14px] dark:border-white/10">
          {confirmed ? (
            <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[8px]">
              <span className="inline-flex items-center gap-[8px] text-[13.5px] font-semibold text-emerald-700 dark:text-emerald-300">
                <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-emerald-500/15">
                  <Check size={13} strokeWidth={3} />
                </span>
                Confirmed
              </span>
              {canAct ? (
                <button type="button" disabled={saving} onClick={onUndo} className={cn(LINK, 'ml-auto')}>
                  Undo
                </button>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-[14px] gap-y-[10px]">
              <button type="button" disabled={!canAct || saving} onClick={onConfirm} className={BTN_DARK}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />}
                Confirm they can sign in
              </button>
              {settingUp ? (
                <span className="text-[12.5px] text-slate-500 dark:text-slate-400">
                  They&apos;re setting it up. You&apos;ll see it here when they tap{' '}
                  <ButtonName>I&apos;ve done it</ButtonName>.
                </span>
              ) : null}
            </div>
          )}
          {!settingUp && canAct ? (
            <button type="button" disabled={sendingBack} onClick={() => setAsking(true)} className={cn(LINK, 'mt-[12px]')}>
              <Undo2 size={12} /> Not set up? Send them back to set it up
            </button>
          ) : null}
        </div>
      </div>

      {asking ? (
        <SendBackDialog
          busy={sendingBack}
          onCancel={() => setAsking(false)}
          onConfirm={async () => {
            const ok = await onSendBack();
            if (ok !== false) setAsking(false);
          }}
        />
      ) : null}
    </div>
  );
};

export default VaGovStep;
